// Feature: features/super-admin-dashboard.md
import React, { useState } from 'react';
import {
  Box,
  Flex,
  Grid,
  GridItem,
  SimpleGrid,
  Heading,
  Text,
  Card,
  CardHeader,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Progress,
  Collapse,
  HStack,
  VStack,
  Divider,
  Tooltip as ChakraTooltip
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import SuperAdminIntegrationSyncPanel from './SuperAdminIntegrationSyncPanel';
import SuperAdminErrorsTriagePanel from './SuperAdminErrorsTriagePanel';

const HIGH_CODING_RATE_THRESHOLD = 95;
const WARNING_CODING_RATE_THRESHOLD = 80;

const ORG_DASHBOARD_LINK = {
  NMA: { label: 'Full NMA Workflow', path: '/nma-workflow', casePrefix: '/ionm-strict-analysis/' },
  USN: { label: 'Open USN Dashboard', path: '/usn-dashboard', casePrefix: '/usn-case/' },
  MPH: { label: 'Open MPH SA Quality', path: '/mph-sa-quality', casePrefix: '/mph-surgical-assist/' }
};

export const formatShortDate = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const year = Number(parts[0]);
  const month = Number(parts[1]) - 1;
  const day = Number(parts[2]);
  const dateObj = new Date(Date.UTC(year, month, day));

  const now = new Date();
  const utcTodayStr = now.toISOString().slice(0, 10);
  const yesterday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 1));
  const utcYesterdayStr = yesterday.toISOString().slice(0, 10);

  const mmdd = `${String(month + 1).padStart(2, '0')}/${String(day).padStart(2, '0')}`;
  const weekday = dateObj.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });

  if (dateStr === utcTodayStr) return `Today (${mmdd})`;
  if (dateStr === utcYesterdayStr) return `Yesterday (${mmdd})`;
  return `${weekday} (${mmdd})`;
};

export const formatRelativeTime = (isoString) => {
  if (!isoString) return 'N/A';
  const target = new Date(isoString.endsWith('Z') ? isoString : `${isoString}Z`);
  const diffMinutes = Math.max(0, Math.floor((Date.now() - target.getTime()) / 60000));
  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
};

export const formatTimestamp = (isoString) => {
  if (!isoString) return '';
  const target = new Date(isoString.endsWith('Z') ? isoString : `${isoString}Z`);
  return target.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit'
  });
};

export const getRateColorScheme = (rate, totalCreated) => {
  if (totalCreated === 0) return 'gray';
  if (rate >= HIGH_CODING_RATE_THRESHOLD) return 'green';
  if (rate >= WARNING_CODING_RATE_THRESHOLD) return 'yellow';
  return 'red';
};

const SuperAdminOrgPulseView = ({ orgKey = 'NMA', orgPulse, daysWindow = 5 }) => {
  const navigate = useNavigate();
  const [expandedDates, setExpandedDates] = useState({});

  const toggleDateExpand = (dateStr) => {
    setExpandedDates((prev) => ({ ...prev, [dateStr]: !prev[dateStr] }));
  };

  const summary = orgPulse?.summary || {
    total_created: 0,
    total_coded: 0,
    total_uncoded: 0,
    overall_coding_rate: 0,
    active_users_count: 0,
    sync_failed_count: 0,
    upload_failed_count: 0
  };
  const dailyStats = orgPulse?.daily_stats || [];
  const recentLogins = orgPulse?.recent_logins || [];
  const errorDiagnostics = orgPulse?.error_diagnostics || [];
  const codingTypeCounts = orgPulse?.coding_type_counts || {};
  const chartData = [...dailyStats].reverse();
  const navCfg = ORG_DASHBOARD_LINK[orgKey] || ORG_DASHBOARD_LINK.NMA;

  return (
    <Box>
      {/* Top KPI Summary Strip */}
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4} mb={6}>
        <Card shadow="sm" border="1px solid" borderColor="gray.200" bg="white">
          <CardBody py={4}>
            <Stat>
              <StatLabel fontSize="xs" color="gray.500" textTransform="uppercase">
                Cases Created ({daysWindow}d)
              </StatLabel>
              <StatNumber fontSize="2xl" color="gray.800">
                {summary.total_created}
              </StatNumber>
              <StatHelpText mb={0} fontSize="xs" color="gray.500">
                Based on external_cases.date_created
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card shadow="sm" border="1px solid" borderColor="gray.200" bg="white">
          <CardBody py={4}>
            <Stat>
              <StatLabel fontSize="xs" color="gray.500" textTransform="uppercase">
                Successfully Coded ({daysWindow}d)
              </StatLabel>
              <HStack align="baseline" spacing={2}>
                <StatNumber fontSize="2xl" color="green.600">
                  {summary.total_coded}
                </StatNumber>
                <Badge
                  colorScheme={getRateColorScheme(summary.overall_coding_rate, summary.total_created)}
                  fontSize="xs"
                >
                  {summary.overall_coding_rate}%
                </Badge>
                {orgPulse?.is_async_coding_org && summary.total_cpt_coded > 0 && (
                  <Badge colorScheme="purple" fontSize="xs">
                    {summary.total_cpt_coded} CPT
                  </Badge>
                )}
              </HStack>
              <StatHelpText mb={0} fontSize="xs" color="gray.500" noOfLines={1}>
                {Object.keys(codingTypeCounts).length > 0
                  ? Object.entries(codingTypeCounts).map(([k, v]) => `${k} (${v})`).join(', ')
                  : `Any coding analysis_type`}
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card shadow="sm" border="1px solid" borderColor="gray.200" bg="white">
          <CardBody py={4}>
            <Stat>
              <StatLabel fontSize="xs" color="gray.500" textTransform="uppercase">
                Uncoded / Pipeline Issues ({daysWindow}d)
              </StatLabel>
              <HStack align="baseline" spacing={2}>
                <StatNumber
                  fontSize="2xl"
                  color={summary.total_uncoded > 0 ? 'orange.500' : 'green.600'}
                >
                  {summary.total_uncoded} uncoded
                </StatNumber>
                {(summary.sync_failed_count > 0 || summary.upload_failed_count > 0) && (
                  <Badge colorScheme="red" fontSize="xs">
                    {summary.sync_failed_count} pipe err / {summary.upload_failed_count} up err
                  </Badge>
                )}
              </HStack>
              <StatHelpText mb={0} fontSize="xs" color="gray.500">
                {errorDiagnostics.length > 0
                  ? `${errorDiagnostics.length} diagnostic item(s) below`
                  : 'All created cases coded'}
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card shadow="sm" border="1px solid" borderColor="gray.200" bg="white">
          <CardBody py={4}>
            <Stat>
              <StatLabel fontSize="xs" color="gray.500" textTransform="uppercase">
                Active Customer Users ({daysWindow}d)
              </StatLabel>
              <StatNumber fontSize="2xl" color="blue.600">
                {summary.active_users_count}
              </StatNumber>
              <StatHelpText mb={0} fontSize="xs" color="gray.500">
                Excludes bots, @revedy.io & impersonation
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* 2-Column Main View */}
      <Grid templateColumns={{ base: '1fr', lg: '7fr 5fr' }} gap={6}>
        {/* Left Column: Daily Case Volume & Coding */}
        <GridItem>
          <Card shadow="sm" border="1px solid" borderColor="gray.200" bg="white">
            <CardHeader pb={2}>
              <Flex justify="space-between" align="center">
                <Box>
                  <Heading size="sm" color="gray.800">
                    {orgPulse?.org_name || orgKey} — Daily Case Volume & Coding Status
                  </Heading>
                  <Text fontSize="xs" color="gray.500" mt={0.5}>
                    Cases by create date vs. coding analysis records in external_case_analysis_results
                  </Text>
                </Box>
                <Button
                  size="xs"
                  variant="ghost"
                  colorScheme="blue"
                  rightIcon={<ExternalLinkIcon />}
                  onClick={() => navigate(navCfg.path)}
                >
                  {navCfg.label}
                </Button>
              </Flex>
            </CardHeader>

            <CardBody pt={2}>
              <Box h="190px" w="100%" mb={4}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 10, right: 15, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(val) => val.slice(5).replace('-', '/')}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <RechartsTooltip
                      labelFormatter={formatShortDate}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0' }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar dataKey="coded_count" name="Successfully Coded" stackId="cases" fill="#38A169" />
                    <Bar
                      dataKey="uncoded_count"
                      name="Uncoded / Pending"
                      stackId="cases"
                      fill="#DD6B20"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>

              <Divider mb={3} />

              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th color="gray.500" fontSize="xs">Create Date</Th>
                      <Th color="gray.500" fontSize="xs" isNumeric>Created</Th>
                      <Th color="gray.500" fontSize="xs" isNumeric>Coded</Th>
                      <Th color="gray.500" fontSize="xs" isNumeric>Uncoded</Th>
                      <Th color="gray.500" fontSize="xs" minW="150px">Coding Pulse</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {dailyStats.map((day) => {
                      const isExpanded = Boolean(expandedDates[day.date]);
                      const hasUncoded = day.uncoded_count > 0;
                      const rateScheme = getRateColorScheme(day.coding_rate, day.total_created);

                      return (
                        <React.Fragment key={day.date}>
                          <Tr
                            _hover={{ bg: 'gray.50' }}
                            cursor={hasUncoded ? 'pointer' : 'default'}
                            onClick={() => hasUncoded && toggleDateExpand(day.date)}
                          >
                            <Td fontWeight="medium" color="gray.800" py={3}>
                              {formatShortDate(day.date)}
                            </Td>
                            <Td isNumeric fontWeight="bold" color="gray.700">
                              {day.total_created}
                            </Td>
                            <Td isNumeric fontWeight="semibold" color="green.600">
                              {day.coded_count}
                            </Td>
                            <Td isNumeric>
                              {hasUncoded ? (
                                <Button
                                  size="xs"
                                  colorScheme="orange"
                                  variant="subtle"
                                  rightIcon={isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleDateExpand(day.date);
                                  }}
                                >
                                  {day.uncoded_count}
                                </Button>
                              ) : (
                                <Text color="gray.400">0</Text>
                              )}
                            </Td>
                            <Td>
                              {day.total_created === 0 ? (
                                <Text fontSize="xs" color="gray.400">No cases created</Text>
                              ) : (
                                <HStack spacing={2}>
                                  <Progress
                                    value={day.coding_rate}
                                    size="sm"
                                    colorScheme={rateScheme}
                                    borderRadius="full"
                                    flex={1}
                                    bg="gray.100"
                                  />
                                  <Badge colorScheme={rateScheme} minW="48px" textAlign="center">
                                    {day.coding_rate}%
                                  </Badge>
                                </HStack>
                              )}
                            </Td>
                          </Tr>

                          {hasUncoded && (
                            <Tr>
                              <Td colSpan={5} p={0} borderBottom={isExpanded ? '1px solid' : 'none'} borderColor="gray.200">
                                <Collapse in={isExpanded} animateOpacity>
                                  <Box bg="orange.50" px={4} py={3}>
                                    <Text fontSize="xs" fontWeight="bold" color="orange.800" mb={2}>
                                      Uncoded Cases on {formatShortDate(day.date)} ({day.uncoded_cases.length}):
                                    </Text>
                                    <VStack align="stretch" spacing={1.5}>
                                      {day.uncoded_cases.map((uCase) => {
                                        const targetId = orgKey === 'NMA' ? uCase.external_case_id : uCase.id;
                                        return (
                                          <Flex
                                            key={uCase.id}
                                            justify="space-between"
                                            align="center"
                                            bg="white"
                                            p={2}
                                            borderRadius="md"
                                            border="1px solid"
                                            borderColor="orange.200"
                                            gap={2}
                                          >
                                            <Box>
                                              <HStack spacing={2}>
                                                <Text fontSize="xs" fontWeight="bold" color="gray.800">
                                                  {uCase.external_case_id}
                                                </Text>
                                                <Badge colorScheme="gray" fontSize="2xs">
                                                  files: {uCase.file_count ?? 0}
                                                </Badge>
                                                {uCase.sync_status && (
                                                  <Badge colorScheme="orange" fontSize="2xs">
                                                    sync: {uCase.sync_status}
                                                  </Badge>
                                                )}
                                              </HStack>
                                              <Text fontSize="2xs" color="red.600" mt={0.5}>
                                                {uCase.primary_reason}
                                              </Text>
                                            </Box>
                                            <ChakraTooltip label="Open case analysis">
                                              <Button
                                                size="2xs"
                                                colorScheme="orange"
                                                variant="outline"
                                                rightIcon={<ExternalLinkIcon />}
                                                onClick={() => navigate(`${navCfg.casePrefix}${targetId}`)}
                                              >
                                                Open
                                              </Button>
                                            </ChakraTooltip>
                                          </Flex>
                                        );
                                      })}
                                    </VStack>
                                  </Box>
                                </Collapse>
                              </Td>
                            </Tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            </CardBody>
          </Card>
        </GridItem>

        {/* Right Column: Recent User Logins */}
        <GridItem>
          <Card shadow="sm" border="1px solid" borderColor="gray.200" bg="white" h="100%">
            <CardHeader pb={2}>
              <Flex justify="space-between" align="center">
                <Box>
                  <Heading size="sm" color="gray.800">
                    Recent User Logins ({daysWindow}d)
                  </Heading>
                  <Text fontSize="xs" color="gray.500" mt={0.5}>
                    Ordered most recent first • Excludes bots & impersonation
                  </Text>
                </Box>
                <Badge colorScheme="blue" borderRadius="full" px={2}>
                  {recentLogins.length} active
                </Badge>
              </Flex>
            </CardHeader>

            <CardBody pt={2}>
              {recentLogins.length === 0 ? (
                <Flex direction="column" justify="center" align="center" py={12} color="gray.500">
                  <Text fontSize="sm" fontWeight="medium">
                    No customer user logins in the last {daysWindow} days.
                  </Text>
                  <Text fontSize="xs" color="gray.400" mt={1}>
                    Automated bot and super-admin impersonation sessions are filtered out.
                  </Text>
                </Flex>
              ) : (
                <Box overflowX="auto">
                  <Table variant="simple" size="sm">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th color="gray.500" fontSize="xs">User</Th>
                        <Th color="gray.500" fontSize="xs">Role</Th>
                        <Th color="gray.500" fontSize="xs">Last Active</Th>
                        <Th color="gray.500" fontSize="xs" isNumeric>Activity ({daysWindow}d)</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {recentLogins.map((u) => (
                        <Tr key={u.user_id} _hover={{ bg: 'gray.50' }}>
                          <Td py={3}>
                            <VStack align="flex-start" spacing={0}>
                              <Text fontWeight="semibold" fontSize="sm" color="gray.800">
                                {u.name}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                {u.email}
                              </Text>
                            </VStack>
                          </Td>
                          <Td>
                            <Badge
                              colorScheme={u.role === 'admin' || u.role === 'client_admin' ? 'purple' : 'gray'}
                              fontSize="2xs"
                            >
                              {u.role}
                            </Badge>
                          </Td>
                          <Td>
                            <VStack align="flex-start" spacing={0}>
                              <Badge colorScheme="green" variant="subtle" fontSize="xs">
                                {formatRelativeTime(u.last_login_at)}
                              </Badge>
                              <Text fontSize="2xs" color="gray.500" mt={0.5}>
                                {formatTimestamp(u.last_login_at)}
                              </Text>
                            </VStack>
                          </Td>
                          <Td isNumeric>
                            <VStack align="flex-end" spacing={0}>
                              <Text fontSize="xs" fontWeight="medium" color="gray.700">
                                {u.active_days} {u.active_days === 1 ? 'day' : 'days'}
                              </Text>
                              <Text fontSize="2xs" color="gray.400">
                                {u.activity_count} actions
                              </Text>
                            </VStack>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </CardBody>
          </Card>
        </GridItem>
      </Grid>

      {errorDiagnostics.length > 0 && (
        <Box mt={6}>
          <SuperAdminErrorsTriagePanel diagnostics={errorDiagnostics} initialOrgFilter={orgKey} />
        </Box>
      )}

      <Divider my={8} borderColor="gray.300" />
      <SuperAdminIntegrationSyncPanel org={orgKey} daysWindow={daysWindow} />
    </Box>
  );
};

export default SuperAdminOrgPulseView;
